import {
    Badge,
    BookmarkButton,
    CategoryBadge,
    dateCountDown,
    ImageOrPlaceholder,
    useTranslations,
} from 'react-ui';
import { BalloonEventModel, getFrontendUrl } from 'ts-core';
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { formatDate } from '../utils/date';
import { trackMixpanel } from '../utils/mixpanel';
import { useEventInteraction } from '../hooks/useEventInteraction';
import {CategoryBadges} from "./CategoryBadges.tsx";
import {DateBadge} from "./DateBadge.tsx";
import {CountdownBadge} from "./CountdownBadge.tsx";
import {useTileSize} from "../hooks/use-tile-size.ts";
import {useEventClick} from "../hooks/useEventClick.tsx";

export interface EventTileProps {
    event: BalloonEventModel;
    selected: boolean;
    hasSelectedEvent: boolean;
}

function getWebPlatformLink(event: BalloonEventModel) {
    const frontendBaseUrl = getFrontendUrl();
    return `${frontendBaseUrl}/event/${event.slug}`;
}
export const EventTile: React.FC<EventTileProps> = memo(function EventTile({
    event,
    selected,
    hasSelectedEvent,
}) {
    const { hovered, onMouseEnter, onMouseLeave, onClick, eventRef } = useEventInteraction(event);
    const { openDetailsOnWebPlatform } = useBalloonEventsOptions();

    const { translated, language } = useTranslations();
    const TILE_SIZE_PX = useTileSize();
    const {handleClick} = useEventClick(event, onClick)
    const highlighted = hovered || selected;
    const shouldDim = !highlighted && hasSelectedEvent;

    // Memoize inline styles to prevent new object creation on every render
    const tileStyle = useMemo(() => ({
        width: TILE_SIZE_PX,
        height: TILE_SIZE_PX,
        minWidth: TILE_SIZE_PX,
        minHeight: TILE_SIZE_PX
    }), [TILE_SIZE_PX]);

    /*const handleClick = () => {
        trackMixpanel("EventTile", "Click", {
            eventId: event.id,
            eventName: event.title,
        });
        if(selected) {
            console.log("------------", window.location)
            /!*window.location.replace(openDetailsOnWebPlatform
                ? getWebPlatformLink(event)
                : '/event/' + event.slug)*!/
        } else {
            //window.location.replace('/event/' + event.slug)
        }
        onClick();
    };*/

    const TileContent = (
        <div
            ref={eventRef}
            className={`event-tile relative bg-white rounded-[10px] overflow-hidden transition-all duration-300 ${
                highlighted ? 'shadow-xl scale-105' : 'shadow-md'
            } ${selected ? 'ring-2 ring-white border-balloon border-[4px]' : ''} ${
                shouldDim ? 'opacity-30' : 'opacity-100'
            }`}
            style={tileStyle}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={handleClick}
        >
            {/* Background Image */}
            <ImageOrPlaceholder
                event={event}
                size={TILE_SIZE_PX} // Convert px back to Tailwind units
                className="absolute left-0 object-cover w-full h-full"
                style={tileStyle}
                hideControls
            />
            
            {/* Gradient Overlay */}
            <div 
                className="left-0 top-0 absolute opacity-75 bg-linear-270 rounded-[10px]"
                style={tileStyle}
            />

            {/* Bookmark Button (top-right) */}
            <div className="bookmark-button-container absolute right-2 top-2 z-10">
                <BookmarkButton event={event} trackingSource="event-tile" />
            </div>

            {/* Countdown Badge (top-left) */}
            <CountdownBadge event={event} className={"absolute left-2 top-2 z-10"}/>
            <CategoryBadges categories={event.categories} className={"absolute left-2 top-[38px] z-10"} language={language} />

            {/* Bottom Section: Title, Location, Date */}
            <div className="absolute bottom-0 left-0 right-0 p-2 z-10">
                {/* Title */}
                <h3 className="text-base font-bold text-white leading-tight drop-shadow-md line-clamp-1 font-roboto">
                    {event.title}
                </h3>

                {/* Location */}
                {event.locationName && (
                    <div className="flex items-center gap-1 mt-1">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="white"
                            className="w-2.5 h-3 flex-shrink-0"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                            />
                        </svg>
                        <span className="text-xs text-white line-clamp-1 drop-shadow-md font-roboto">
                            {event.locationName}
                        </span>
                    </div>
                )}

                {/* Date Badge */}
                <DateBadge event={event}/>
            </div>
        </div>
    );
    return TileContent;
    // Wrap in Link if selected
    /*if (selected) {
        return (
            <Link
                className="block"
                target={openDetailsOnWebPlatform ? '_blank' : '_self'}
                to={
                    openDetailsOnWebPlatform
                        ? getWebPlatformLink(event)
                        : '/event/' + event.slug
                }
            >
                {TileContent}
            </Link>
        );
    }

    return TileContent;*/
});
